import React, { useEffect, useMemo, useState } from 'react';
import type { OrgMember } from '@/api/services/organizations';
import { workspaceSourcesService, workspaceSourcesToPickerSources } from '@/api/services/workspaceSources';
import {
  CUSTOMER_WORKSPACE_ROLE_OPTIONS,
  canEditWorkspaceMembershipRole,
  normalizeWorkspaceRole,
  platformRoleLabel,
  platformStaffBadgeLabel,
  workspaceRoleLabel,
} from '@/utils/workspaceRoles';
import { isPlatformStaffRole } from '@/utils/teamRoles';
import SourceAssignmentPicker, { normalizeSourceId } from './SourceAssignmentPicker';
import { ToggleField } from '@/components/ui';
import { formatApiError } from '@/utils/formatApiError';
import type { Source } from '@/types';

const MEMBERSHIP_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
] as const;

export type WorkspaceMembershipSavePayload = {
  membershipRole: string;
  membershipStatus: string;
  accountEnabled?: boolean;
  sourceIds?: string[];
};

type Props = {
  member: OrgMember;
  workspaceName?: string;
  canManageAccount: boolean;
  onClose: () => void;
  onSave: (payload: WorkspaceMembershipSavePayload) => Promise<void>;
  onRemove?: () => void;
};

function isWorkspaceAdminRole(role: string): boolean {
  return normalizeWorkspaceRole(role) === 'workspace_admin';
}

export default function EditWorkspaceMembershipModal({
  member,
  workspaceName,
  canManageAccount,
  onClose,
  onSave,
  onRemove,
}: Props) {
  const roleEditable = canEditWorkspaceMembershipRole(member);
  const [membershipRole, setMembershipRole] = useState(member.membershipRole);
  const [membershipStatus, setMembershipStatus] = useState(member.status ?? 'active');
  const [accountEnabled, setAccountEnabled] = useState(member.accountEnabled ?? true);
  const [workspaceSources, setWorkspaceSources] = useState<Source[]>([]);
  const [defaultMemberSourceIds, setDefaultMemberSourceIds] = useState<string[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<Set<string>>(new Set());
  const [loadingSources, setLoadingSources] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const label = member.name?.trim() || member.email || 'this member';
  const staffMember = member.platformRole ? isPlatformStaffRole(member.platformRole) : false;
  const adminRole = isWorkspaceAdminRole(membershipRole);

  const enabledWorkspaceSources = useMemo(
    () => workspaceSources.filter((source) => source.is_enabled),
    [workspaceSources],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadSources() {
      setLoadingSources(true);
      setError('');
      try {
        const settings = await workspaceSourcesService.getSettings();
        if (cancelled) return;
        const enabledIds = new Set(settings.enabledSourceIds.map(normalizeSourceId));
        const mapped = workspaceSourcesToPickerSources(
          settings.availableSources.filter((source) => enabledIds.has(normalizeSourceId(source.id))),
        );
        setWorkspaceSources(mapped);
        setDefaultMemberSourceIds(settings.defaultMemberSourceIds);
        const assigned = new Set(
          (member.sourceAccess?.sources ?? []).map((source) => normalizeSourceId(source.id)),
        );
        if (isWorkspaceAdminRole(member.membershipRole)) {
          mapped.forEach((source) => assigned.add(normalizeSourceId(source.id)));
        } else if (assigned.size === 0) {
          settings.defaultMemberSourceIds.forEach((id) => assigned.add(normalizeSourceId(id)));
        }
        setSelectedSourceIds(assigned);
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err, 'Failed to load workspace sources'));
        }
      } finally {
        if (!cancelled) setLoadingSources(false);
      }
    }
    void loadSources();
    return () => {
      cancelled = true;
    };
  }, [member.id, member.membershipRole, member.sourceAccess?.sources]);

  useEffect(() => {
    if (loadingSources) return;
    if (adminRole) {
      setSelectedSourceIds(new Set(enabledWorkspaceSources.map((source) => normalizeSourceId(source.id))));
      return;
    }
    if (selectedSourceIds.size > 0) return;
    setSelectedSourceIds(new Set(defaultMemberSourceIds.map(normalizeSourceId)));
  }, [adminRole, defaultMemberSourceIds, enabledWorkspaceSources, loadingSources, selectedSourceIds.size]);

  function toggleSource(sourceId: string) {
    if (adminRole) return;
    setSelectedSourceIds((prev) => {
      const next = new Set(prev);
      const key = normalizeSourceId(sourceId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectAllSources() {
    setSelectedSourceIds(new Set(enabledWorkspaceSources.map((source) => normalizeSourceId(source.id))));
  }

  function clearAllSources() {
    setSelectedSourceIds(new Set());
  }

  function useWorkspaceDefaults() {
    setSelectedSourceIds(new Set(defaultMemberSourceIds.map(normalizeSourceId)));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const sourceIds = adminRole
        ? undefined
        : enabledWorkspaceSources
            .filter((source) => selectedSourceIds.has(normalizeSourceId(source.id)))
            .map((source) => source.id);
      await onSave({
        membershipRole,
        membershipStatus,
        accountEnabled: canManageAccount ? accountEnabled : undefined,
        sourceIds,
      });
    } catch (err) {
      setError(formatApiError(err, 'Save failed'));
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal card card-elevated edit-workspace-membership-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="edit-workspace-membership-title"
      >
        <h2 id="edit-workspace-membership-title">Edit membership</h2>
        <p className="muted modal-hint">
          Update workspace access for <strong>{label}</strong>
          {workspaceName ? (
            <>
              {' '}
              in <strong>{workspaceName}</strong>
            </>
          ) : null}
          . Account details are shared across all workspaces.
        </p>

        <section className="edit-workspace-membership-modal__section">
          <dl className="edit-workspace-membership-modal__summary">
            <div>
              <dt>Email</dt>
              <dd>{member.email ?? '—'}</dd>
            </div>
            <div>
              <dt>Name</dt>
              <dd>{member.name ?? '—'}</dd>
            </div>
            <div>
              <dt>Platform role</dt>
              <dd>
                {platformRoleLabel(member.platformRole)}
                {staffMember ? (
                  <span className="badge badge--platform-staff"> {platformStaffBadgeLabel(member.platformRole)}</span>
                ) : null}
              </dd>
            </div>
          </dl>
        </section>

        <form onSubmit={(e) => void handleSave(e)}>
          <section className="edit-workspace-membership-modal__section">
            <div className="formGrid">
              <label>
                Workspace role
                {roleEditable ? (
                  <select
                    value={membershipRole}
                    onChange={(e) => setMembershipRole(e.target.value)}
                    aria-label="Workspace role"
                  >
                    {CUSTOMER_WORKSPACE_ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div>{workspaceRoleLabel(member.membershipRole, { platformRole: member.platformRole })}</div>
                )}
              </label>
              <label>
                Membership status
                <select
                  value={membershipStatus}
                  onChange={(e) => setMembershipStatus(e.target.value)}
                  aria-label="Membership status"
                  disabled={!roleEditable}
                >
                  {MEMBERSHIP_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {canManageAccount ? (
              <div className="toggleRow">
                <ToggleField checked={accountEnabled} onChange={setAccountEnabled} label="Account enabled" />
              </div>
            ) : null}
          </section>

          <section className="edit-workspace-membership-modal__section">
            <h3>Member source access</h3>
            <p className="muted">Sources this member can use when creating searches.</p>
            {loadingSources ? (
              <p className="muted">Loading workspace sources…</p>
            ) : enabledWorkspaceSources.length === 0 ? (
              <p className="muted">
                No workspace sources enabled yet. Enable sources in Workspace settings.
              </p>
            ) : adminRole ? (
              <p className="muted">
                Workspace admins can use all {enabledWorkspaceSources.length} workspace enabled sources.
              </p>
            ) : (
              <SourceAssignmentPicker
                sources={enabledWorkspaceSources}
                selected={selectedSourceIds}
                onToggle={toggleSource}
                onSelectAll={selectAllSources}
                onClearAll={clearAllSources}
                legend="Assigned sources"
                hideLegend
                helperText={`${selectedSourceIds.size} of ${enabledWorkspaceSources.length} workspace sources`}
                summaryFormat="fraction"
                groupByCategory
              />
            )}
            {!adminRole && enabledWorkspaceSources.length > 0 ? (
              <div className="table-action-bar">
                <button type="button" className="btn btn-sm" onClick={useWorkspaceDefaults}>
                  Use workspace defaults
                </button>
              </div>
            ) : null}
          </section>

          {error ? <div className="alert alert-error">{error}</div> : null}

          <div className="modal-actions">
            {onRemove && roleEditable ? (
              <button type="button" className="btn btn-danger" onClick={onRemove} disabled={submitting}>
                Remove from workspace
              </button>
            ) : null}
            <span className="modal-actions__spacer" />
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting || !roleEditable || loadingSources}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
